/**
 * Conventional aluminum-alloy composition order (Aluminum Association / EN style).
 * Al first as the base metal, then Si–Ti, then common residuals / modifiers.
 * Unknown symbols sort alphabetically after the known list.
 */
export const ALUMINUM_ALLOY_ELEMENT_ORDER = [
  'Al',
  'Si',
  'Fe',
  'Cu',
  'Mn',
  'Mg',
  'Cr',
  'Ni',
  'Zn',
  'Ti',
  'Sn',
  'Pb',
  'Bi',
  'Sb',
  'Sr',
  'Zr',
  'V',
  'B',
  'Be',
  'Li',
  'Cd',
  'Co',
  'Ga',
  'Ag',
  'Ca',
  'Na',
  'P',
  'Sc',
  'Y',
  'La',
  'Ce',
  'Nd',
  'Hf',
  'Mo',
  'W',
  'Nb',
  'Ta',
  'In',
  'Ge',
  'As',
]

const ORDER_INDEX = ALUMINUM_ALLOY_ELEMENT_ORDER.reduce((acc, symbol, index) => {
  acc[symbol.toLowerCase()] = index
  return acc
}, {})

/**
 * Extract a comparable element symbol from a field title/name (e.g. "Si", "si", "Si wt%").
 */
export function normalizeElementSymbol(value) {
  if (value == null) return ''
  const raw = String(value).trim()
  if (!raw) return ''
  const match = raw.match(/^([A-Za-z]{1,2})/)
  return match ? match[1] : raw
}

function elementRank(value) {
  const symbol = normalizeElementSymbol(value)
  if (!symbol) return Number.POSITIVE_INFINITY
  const known = ORDER_INDEX[symbol.toLowerCase()]
  return known !== undefined ? known : ALUMINUM_ALLOY_ELEMENT_ORDER.length
}

/**
 * Compare two element labels for traditional Al-alloy ordering.
 */
export function compareAlloyElements(a, b) {
  const rankA = elementRank(a)
  const rankB = elementRank(b)
  if (rankA !== rankB) return rankA - rankB
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })
}

/**
 * Sort composition field objects that have `title` / `name` element labels.
 */
export function sortCompositionFields(fields) {
  return [...(fields || [])].sort((a, b) =>
    compareAlloyElements(a && (a.title || a.name), b && (b.title || b.name))
  )
}

/**
 * Sort an array of element symbol / title strings.
 */
export function sortAlloyElementKeys(keys) {
  return [...(keys || [])].sort(compareAlloyElements)
}
